import JSZip from "jszip";
import {
  isEncryptedDocumentUrl,
  decryptBlob,
} from "../../../utils/documentEncryption";

const sanitizeSegment = (value, fallback = "unknown") => {
  const cleaned = String(value || fallback)
    .trim()
    .replace(/[^a-zA-Z0-9._-]+/g, "_")
    .replace(/_+/g, "_")
    .replace(/^_|_$/g, "");
  return cleaned || fallback;
};

const getExtension = (url, blob) => {
  if (isEncryptedDocumentUrl(url)) return "jpg";
  const fromUrl = url?.match(/\.([a-zA-Z0-9]+)(?:\?|$)/);
  if (fromUrl?.[1]) {
    const ext = fromUrl[1].toLowerCase();
    return ext === "jpeg" ? "jpg" : ext;
  }
  const fromType = blob?.type?.split("/")?.[1];
  if (fromType) return fromType === "jpeg" ? "jpg" : fromType;
  return "jpg";
};

const fetchImageBlob = async (url) => {
  if (!url || typeof url !== "string") return null;
  try {
    const res = await fetch(url);
    if (!res.ok) return null;
    let blob = await res.blob();
    if (isEncryptedDocumentUrl(url)) {
      blob = await decryptBlob(blob);
      if (!blob) return null;
    }
    return blob;
  } catch {
    return null;
  }
};

const pushArrayPhotos = (entries, folder, urls, namePrefix) => {
  (urls || []).forEach((url, index) => {
    if (!url) return;
    const num = String(index + 1).padStart(2, "0");
    entries.push({
      zipPath: `${folder}/${namePrefix}_${num}`,
      url,
    });
  });
};

const pushSinglePhoto = (entries, folder, url, fileName) => {
  if (!url) return;
  entries.push({
    zipPath: `${folder}/${fileName}`,
    url,
  });
};

const pushObjectPhotos = (entries, folder, obj, labelMap = {}) => {
  if (!obj || typeof obj !== "object") return;
  Object.entries(obj).forEach(([key, value]) => {
    if (!value || typeof value !== "string") return;
    const label = labelMap[key] || key;
    pushSinglePhoto(entries, folder, value, sanitizeSegment(label));
  });
};

const collectCustomerPhotoEntries = ({
  survey,
  transportWork,
  civilWork,
  technicalWork,
  commissioningWork,
}) => {
  const entries = [];

  if (survey) {
    const base = "01_survey";
    pushArrayPhotos(
      entries,
      `${base}/roof_inspection`,
      survey.roof_inspection_photos,
      "roof_inspection",
    );
    pushArrayPhotos(
      entries,
      `${base}/house_front_view`,
      survey.house_front_view_photos,
      "house_front_view",
    );
    pushArrayPhotos(entries, `${base}/meter`, survey.meter_photos, "meter");
    pushArrayPhotos(
      entries,
      `${base}/line_diagram`,
      survey.line_diagram_photos,
      "line_diagram",
    );
    pushArrayPhotos(
      entries,
      `${base}/obstacle`,
      survey.obstacle_photos,
      "obstacle",
    );
    pushSinglePhoto(entries, `${base}/documents`, survey.aadhar_photo, "aadhar");
    pushSinglePhoto(
      entries,
      `${base}/documents`,
      survey.bank_passbook_photo,
      "bank_passbook",
    );
    pushSinglePhoto(
      entries,
      `${base}/documents`,
      survey.electricity_bill_photo,
      "electricity_bill",
    );
    pushSinglePhoto(
      entries,
      `${base}/signatures`,
      survey.surveyor_signature_photo,
      "surveyor_signature",
    );
    pushSinglePhoto(
      entries,
      `${base}/signatures`,
      survey.customer_signature_photo,
      "customer_signature",
    );
    pushSinglePhoto(entries, `${base}`, survey.qr_code_photo, "qr_code");
    pushSinglePhoto(
      entries,
      `${base}/tentative_locations`,
      survey.tentative_panel_location_photo,
      "tentative_panel_location",
    );
    pushSinglePhoto(
      entries,
      `${base}/tentative_locations`,
      survey.tentative_inverter_location_photo,
      "tentative_inverter_location",
    );
  }

  if (transportWork) {
    const base = "02_transport";
    pushSinglePhoto(
      entries,
      base,
      transportWork.customer_kit_photo,
      "customer_kit",
    );
    pushSinglePhoto(
      entries,
      base,
      transportWork.panel_structure_photo,
      "panel_structure",
    );
  }

  if (civilWork?.photos) {
    pushObjectPhotos(entries, "03_civil", civilWork.photos, {
      mounting_structure: "mounting_structure",
    });
  }

  if (technicalWork) {
    const base = "04_technical";
    const panels = technicalWork.panels || {};
    Object.entries(panels).forEach(([panelId, panel]) => {
      const url =
        panel?.serialNumberPhoto ||
        panel?.serial_number_photo ||
        panel?.photo;
      if (url) {
        pushSinglePhoto(
          entries,
          `${base}/panels`,
          url,
          `panel_${panelId}_serial`,
        );
      }
    });

    const wiringInverter = technicalWork.wiring_inverter || {};
    pushSinglePhoto(
      entries,
      `${base}/inverter`,
      wiringInverter.inverterSerialPhoto ||
        wiringInverter.inverter_serial_photo,
      "inverter_serial",
    );

    const earthing = technicalWork.earthing || {};
    pushSinglePhoto(entries, `${base}/earthing`, earthing.photo1, "earthing_1");
    pushSinglePhoto(entries, `${base}/earthing`, earthing.photo2, "earthing_2");

    pushObjectPhotos(
      entries,
      `${base}/installation`,
      technicalWork.installation_photos,
      {
        panelSideWithCustomer: "panel_side_with_customer",
        panelFrontWithCustomer: "panel_front_with_customer",
        inverterInstallationPhoto: "inverter_installation",
      },
    );

    pushSinglePhoto(
      entries,
      base,
      technicalWork.customer_photo,
      "customer_photo",
    );
  }

  if (commissioningWork) {
    const base = "05_commissioning";
    pushSinglePhoto(
      entries,
      base,
      commissioningWork.voltage_meter_photo,
      "voltage_meter",
    );
    pushSinglePhoto(
      entries,
      base,
      commissioningWork.main_meter_serial_photo,
      "main_meter_serial",
    );
    pushSinglePhoto(
      entries,
      base,
      commissioningWork.customer_photo_with_panels_side,
      "customer_with_panels_side",
    );
    pushSinglePhoto(
      entries,
      base,
      commissioningWork.customer_photo_with_panels_front,
      "customer_with_panels_front",
    );
    pushSinglePhoto(
      entries,
      base,
      commissioningWork.customer_photo_with_inverter,
      "customer_with_inverter",
    );
  }

  return entries;
};

/**
 * Downloads a zip of all work-phase photos for a customer.
 * @returns {Promise<number>} Number of photos included in the zip
 */
export const downloadCustomerPhotosZip = async (
  customer,
  {
    survey = null,
    transportWork = null,
    civilWork = null,
    technicalWork = null,
    commissioningWork = null,
  } = {},
) => {
  const entries = collectCustomerPhotoEntries({
    survey,
    transportWork,
    civilWork,
    technicalWork,
    commissioningWork,
  });

  if (entries.length === 0) {
    return 0;
  }

  const rootFolder = sanitizeSegment(
    [customer?.cin, customer?.name].filter(Boolean).join("_"),
    "customer_photos",
  );

  const zip = new JSZip();
  const root = zip.folder(rootFolder);
  const usedPaths = new Set();
  let added = 0;

  for (const entry of entries) {
    const blob = await fetchImageBlob(entry.url);
    if (!blob) continue;

    const ext = getExtension(entry.url, blob);
    let zipPath = `${entry.zipPath}.${ext}`;
    if (usedPaths.has(zipPath)) {
      let counter = 2;
      while (usedPaths.has(`${entry.zipPath}_${counter}.${ext}`)) {
        counter += 1;
      }
      zipPath = `${entry.zipPath}_${counter}.${ext}`;
    }
    usedPaths.add(zipPath);
    root.file(zipPath, blob);
    added += 1;
  }

  if (added === 0) {
    return 0;
  }

  const zipBlob = await zip.generateAsync({ type: "blob" });
  const timestamp = new Date().toISOString().slice(0, 10);
  const fileName = `${rootFolder}_photos_${timestamp}.zip`;
  const link = document.createElement("a");
  link.href = URL.createObjectURL(zipBlob);
  link.download = fileName;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(link.href);

  return added;
};
